/**
 * Feature Flag Hook
 * 
 * React hook for accessing feature flags.
 * Automatically fetches flags on mount and when user changes.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../store/auth';
import { getFeatureFlags, getFeatureFlag } from '../services/featureFlags';
import { FEATURE_FLAG_DEFAULTS, FeatureFlagKey } from '@court-sync/shared';

interface UseFeatureFlagsReturn {
  flags: Record<string, boolean>;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

interface UseFeatureFlagReturn {
  enabled: boolean;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

// Cache for feature flags
let flagsCache: Record<string, boolean> | null = null;
let cacheTimestamp: number = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Hook to get all feature flags
 */
export function useFeatureFlags(): UseFeatureFlagsReturn {
  const { user } = useAuthStore();
  const [flags, setFlags] = useState<Record<string, boolean>>(FEATURE_FLAG_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchFlags = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check cache first
      const now = Date.now();
      if (flagsCache && (now - cacheTimestamp) < CACHE_TTL) {
        setFlags(flagsCache);
        setLoading(false);
        return;
      }
      
      const fetchedFlags = await getFeatureFlags();
      flagsCache = fetchedFlags;
      cacheTimestamp = now;
      setFlags(fetchedFlags);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch feature flags');
      setError(error);
      setFlags(FEATURE_FLAG_DEFAULTS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFlags();
  }, [fetchFlags, user?.uid]); // Refetch when user changes

  return {
    flags,
    loading,
    error,
    refetch: fetchFlags,
  };
}

/**
 * Hook to get a specific feature flag
 */
export function useFeatureFlag(key: FeatureFlagKey): UseFeatureFlagReturn {
  const { user } = useAuthStore();
  const [enabled, setEnabled] = useState<boolean>(
    FEATURE_FLAG_DEFAULTS[key] ?? false
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchFlag = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check cache first
      const now = Date.now();
      if (flagsCache && (now - cacheTimestamp) < CACHE_TTL) {
        const cachedValue = flagsCache[key];
        if (cachedValue !== undefined) {
          setEnabled(cachedValue);
          setLoading(false);
          return;
        }
      }
      
      const fetchedValue = await getFeatureFlag(key);
      setEnabled(fetchedValue);
      
      // Update cache
      if (flagsCache) {
        flagsCache[key] = fetchedValue;
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error(`Failed to fetch feature flag ${key}`);
      setError(error);
      setEnabled(FEATURE_FLAG_DEFAULTS[key] ?? false);
    } finally {
      setLoading(false);
    }
  }, [key]);

  useEffect(() => {
    fetchFlag();
  }, [fetchFlag, user?.uid]); // Refetch when user changes

  return {
    enabled,
    loading,
    error,
    refetch: fetchFlag,
  };
}

